"use client";

import clsx from "clsx";
import Highlight, { defaultProps, PrismTheme } from "prism-react-renderer";
import { useEffect, useState } from "react";
import { Language } from "../app/playground/examples";
import {
  gutterStyles,
  locStyles,
  preStyles,
  textAreaStyles,
} from "./CodeEditor.css";
import { Box, BoxProps, Flex } from "./ui";
import { vars } from "./ui/Box/theme.css";

type Props = BoxProps<"div"> & {
  code: string;
  language: Language;
  readOnly?: boolean;
};

const editorTheme = getTheme(vars.colors);

// Styles are from react-simple-code-editor
const CodeEditor = ({
  code: initialCode,
  language,
  readOnly = false,
  ...rest
}: Props) => {
  const [code, setCode] = useState(initialCode);

  useEffect(() => {
    setCode(initialCode);
  }, [initialCode]);

  return (
    <Highlight
      {...defaultProps}
      code={code}
      // @ts-expect-error Not all languages are included in the types
      language={language}
      theme={editorTheme}
    >
      {({ className, style, tokens, getLineProps, getTokenProps }) => {
        const longestLine = Math.max.apply(
          0,
          code.split("\n").map((line) => line.length),
        );
        return (
          <Box
            {...rest}
            css={{
              flex: 1,
              position: "relative",
              overflow: "scroll",
              background: "background",
              fontSize: { sm: 1, md: 2 },
              ...rest.css,
            }}
          >
            <Flex>
              <pre
                className={clsx(gutterStyles, className)}
                style={style}
                aria-hidden="true"
              >
                {tokens.map((line, i) => (
                  <div key={i} {...getLineProps({ line, key: i })}>
                    <span className={locStyles}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                ))}
              </pre>

              <pre style={style} className={clsx(className, preStyles)}>
                {tokens.map((line, i) => (
                  <div key={i} {...getLineProps({ line, key: i })}>
                    {line.map((token, key) => (
                      <span key={key} {...getTokenProps({ token, key })} />
                    ))}
                  </div>
                ))}
              </pre>
              <textarea
                readOnly={readOnly}
                rows={tokens.length}
                cols={longestLine}
                className={textAreaStyles}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                data-gramm={false}
              />
            </Flex>
          </Box>
        );
      }}
    </Highlight>
  );
};

// https://github.com/component-driven/component-driven-development/blob/master/slides/theme/codeTheme.js
function getTheme({
  // background,
  text,
  variable,
  attribute,
  definition,
  keyword,
  operator,
  property,
  number,
  string,
  comment,
  meta,
  tag,
}: (typeof vars)["colors"]): PrismTheme {
  return {
    // Styles based on https://github.com/conorhastings/react-syntax-highlighter/
    // Colors are custom
    plain: {
      // color: text,
      // backgroundColor: background,
    },
    styles: [
      {
        types: ["symbol", "entity", "keyword", "atrule"],
        style: {
          color: keyword,
          fontStyle: "italic",
          fontWeight: "bold",
        },
      },
      {
        types: ["constant", "boolean", "string", "char"],
        style: {
          color: string,
        },
      },
      {
        types: [
          "attr-name",
          "attr-value",
          "selector",
          "variable",
          "class-name",
        ],
        style: {
          color: attribute,
        },
      },
      {
        types: ["variable"],
        style: {
          color: variable,
        },
      },
      {
        types: ["property", "property-access"],
        style: {
          color: property,
          fontWeight: "normal",
        },
      },
      {
        types: ["tag"],
        style: {
          color: tag,
        },
      },
      {
        types: ["operator"],
        style: {
          color: operator,
        },
      },
      {
        types: ["number"],
        style: {
          color: number,
        },
      },
      {
        types: [
          "comment",
          "block-comment",
          "prolog",
          "doctype",
          "cdata",
          "punctuation",
        ],
        style: {
          color: comment,
        },
      },
      {
        types: ["function", "function-name"],
        style: {
          color: definition,
          fontWeight: "normal",
        },
      },
      {
        types: ["builtin", "attr-value"],
        style: {
          color: text,
        },
      },
      {
        types: ["url"],
        style: {
          color: meta,
        },
      },
      {
        types: ["important"],
        style: {
          fontWeight: "normal",
        },
      },
      {
        types: ["bold"],
        style: {
          fontWeight: "bold",
        },
      },
      {
        types: ["italic"],
        style: {
          fontStyle: "italic",
        },
      },
      {
        types: ["namespace"],
        style: {
          opacity: 0.7,
        },
      },
    ],
  };
}

export default CodeEditor;
